import { LiqTxDetailDialog } from "@/components/agents/LiqTxDetailDialog";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { InfoExplain } from "@/components/ui/InfoExplain";
import {
	AGENT_GATEWAY_CANISTER_ID,
	loadScanIntervalMin,
	saveScanIntervalMin,
	useAgentGatewayConfigured,
	useAgentGatewayStats,
	useBuyCycleCreditsWithIcp,
	useCreateApiKey,
	useMyAgent,
	useMyApiKeys,
	useMyCycleCredits,
	useMyHumanWaiversRemaining,
	useMyAgentCallHistory,
	useMyAgentTxHistory,
	useMyHuntHfRange,
	useMyHuntTargets,
	useAgentCancelOrder,
	useAgentPlaceLimit,
	useRegisterAgentAsHuman,
	useRevokeApiKey,
	useSetAgentActive,
	useSetHuntHfRange,
	useSetHuntTargets,
	type AgentCollateral,
	type AgentTxHistoryRow,
	type HuntComboId,
	type HuntTargetUi,
} from "@/hooks/useAgentGateway";
import {
	useMmkBookDepth,
	useMyMmkOrders,
} from "@/hooks/useMmkBook";
import { getMmkBookCanisterId } from "@/lib/mmk-book-actor";
import { getEnvMap } from "@/lib/network";
import {
	notifyAutopilotChanged,
	useAgentHuntScanner,
} from "@/hooks/useAgentHuntScanner";
import {
	AGENT_ACTIVE_HELP,
	AGENT_FEE_SUSTAINABILITY,
	AGENT_FLEET_BLURB,
	AGENT_KEY_SECURITY,
	agentHuntHelpText,
	loadHuntSettingsUpdatedAt,
	saveHuntSettingsUpdatedAt,
} from "@/lib/agent-layer-copy";
import {
	DEFAULT_AUTOPILOT_DAILY_CAP_USDM,
	getAutopilotSpentTodayUsdm,
	loadAutopilotEnabled,
	saveAutopilotEnabled,
} from "@/lib/agent-autopilot";
import {
	aggregateSeizedFromTxHistory,
	formatLiqTxActivitySub,
	formatTokenE8,
} from "@/lib/agent-tx-detail";
import { useAuth } from "@/lib/auth";
import { copyToClipboard } from "@/lib/clipboard";
import {
	loadHuntScanActivity,
	stripFeePrefix,
	subscribeHuntScanActivity,
	type HuntScanActivity,
} from "@/lib/hunt-activity-log";
import {
	defaultHuntTargets,
	e8ToHf,
	hfToE8,
	HUNT_HF_CAP,
	HUNT_HF_FLOOR,
} from "@/lib/hunt-match";
import { docsArticlePath } from "@/lib/docs/parse";
import { AGENT_LAYER_SLOGAN, ROUTES } from "@/lib/routes";
import { cn, formatAmountPlain, parseAmount } from "@/lib/utils";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const HISTORY_PAGE_SIZE = 20;

function pageRangeLabel(page: number, pageSize: number, total: number): string {
	if (total <= 0) return "Page 1 · showing 0 of 0";
	const start = page * pageSize + 1;
	const end = Math.min(total, (page + 1) * pageSize);
	return `Page ${page + 1} · showing ${start}–${end} of ${total}`;
}

function HistoryPager({
	page,
	pageSize,
	total,
	onPrev,
	onNext,
}: {
	page: number;
	pageSize: number;
	total: number;
	onPrev: () => void;
	onNext: () => void;
}) {
	const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
	if (total <= pageSize) {
		return (
			<p className="mt-3 font-mono text-[10px] text-yellow-400/45">
				{pageRangeLabel(page, pageSize, total)}
			</p>
		);
	}
	return (
		<div className="mt-3 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px]">
			<span className="text-yellow-400/45">
				{pageRangeLabel(page, pageSize, total)}
			</span>
			<div className="flex gap-2">
				<button
					type="button"
					disabled={page <= 0}
					className="rounded-md border border-yellow-500/40 px-3 py-1 text-yellow-300 disabled:opacity-40"
					onClick={onPrev}
				>
					Prev
				</button>
				<button
					type="button"
					disabled={page >= maxPage}
					className="rounded-md border border-yellow-500/40 px-3 py-1 text-yellow-300 disabled:opacity-40"
					onClick={onNext}
				>
					Next
				</button>
			</div>
		</div>
	);
}

function useHuntScanLog(): HuntScanActivity[] {
	return useSyncExternalStore(
		subscribeHuntScanActivity,
		loadHuntScanActivity,
		() => [],
	);
}

function feeLabel(
	feeKind: "waiver" | "credits" | "cycles" | "none" | null,
	cyclesCharged: number,
): string {
	if (feeKind === "waiver") return "fee=waiver";
	if (feeKind === "credits") return "fee=credits";
	if (feeKind === "cycles") return `fee=cycles(${cyclesCharged})`;
	if (feeKind === "none") return "fee=none";
	if (cyclesCharged === 0) return "fee=?";
	return `cycles=${cyclesCharged}`;
}

function formatNs(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "—";
	const ms = ts > 1e15 ? ts / 1e6 : ts;
	try {
		return new Date(ms).toLocaleString();
	} catch {
		return "—";
	}
}

const COLS: AgentCollateral[] = ["ICP", "ckBTC", "ckETH"];
const COMBOS: { id: HuntComboId; label: string }[] = [
	{ id: "IcpBtc", label: "ICP+BTC" },
	{ id: "EthBtc", label: "ETH+BTC" },
	{ id: "IcpBtcEth", label: "ICP+BTC+ETH" },
];
const INTERVALS = [1, 2, 5, 10, 30] as const;

function hasAsset(targets: HuntTargetUi[], a: AgentCollateral) {
	return targets.some((t) => t.kind === "asset" && t.asset === a);
}
function hasCombo(targets: HuntTargetUi[], c: HuntComboId) {
	return targets.some((t) => t.kind === "combo" && t.combo === c);
}

/** Agent Layer: profile, hunt, keys, bot txs / calls. */
export default function AgentsPage() {
	const { isAuthenticated, login, isLoggingIn, principal, balances, identity } =
		useAuth();
	const gatewayOk = useAgentGatewayConfigured();
	const { data: agent, isLoading: agentLoading } = useMyAgent();
	const { data: keys = [] } = useMyApiKeys();
	const { data: credits = 0n } = useMyCycleCredits();
	const { data: waivers = 0n } = useMyHumanWaiversRemaining();
	const { data: stats } = useAgentGatewayStats();
	const { data: huntSaved = [] } = useMyHuntTargets();
	const { data: hfSaved } = useMyHuntHfRange();
	const register = useRegisterAgentAsHuman();
	const setActive = useSetAgentActive();
	const createKey = useCreateApiKey();
	const revokeKey = useRevokeApiKey();
	const buyCredits = useBuyCycleCreditsWithIcp();
	const setHunt = useSetHuntTargets();
	const setHfRange = useSetHuntHfRange();
	const huntStatus = useAgentHuntScanner();
	const scanLog = useHuntScanLog();
	const [txPageIdx, setTxPageIdx] = useState(0);
	const [callPageIdx, setCallPageIdx] = useState(0);
	const [scanPageIdx, setScanPageIdx] = useState(0);
	const { data: callPage, isLoading: callsLoading } = useMyAgentCallHistory(
		callPageIdx * HISTORY_PAGE_SIZE,
		HISTORY_PAGE_SIZE,
	);
	const { data: txPage, isLoading: txsLoading } = useMyAgentTxHistory(
		txPageIdx * HISTORY_PAGE_SIZE,
		HISTORY_PAGE_SIZE,
	);
	const callHistory = callPage?.events ?? [];
	const txHistory = txPage?.events ?? [];
	const callTotal = callPage?.total ?? 0;
	const txTotal = txPage?.total ?? 0;
	const scanTotal = scanLog.length;
	const scanSlice = useMemo(() => {
		const start = scanPageIdx * HISTORY_PAGE_SIZE;
		return scanLog.slice(start, start + HISTORY_PAGE_SIZE);
	}, [scanLog, scanPageIdx]);

	const [name, setName] = useState("my-agent");
	const [keyLabel, setKeyLabel] = useState("bot");
	const [newSecret, setNewSecret] = useState<string | null>(null);
	const [icpAmount, setIcpAmount] = useState("1");
	const [localHunt, setLocalHunt] = useState<HuntTargetUi[]>(() =>
		defaultHuntTargets(),
	);
	const [scanMin, setScanMin] = useState(5);
	const [hfMin, setHfMin] = useState(String(HUNT_HF_FLOOR.toFixed(2)));
	const [hfMax, setHfMax] = useState(String(HUNT_HF_CAP.toFixed(2)));
	const [autopilot, setAutopilot] = useState(false);
	const [settingsUpdatedAt, setSettingsUpdatedAt] = useState<number | null>(
		null,
	);
	const [detailTx, setDetailTx] = useState<AgentTxHistoryRow | null>(null);
	const [bookSide, setBookSide] = useState<"buy" | "sell">("buy");
	const [bookPrice, setBookPrice] = useState("2.00");
	const [bookSize, setBookSize] = useState("1");
	const bookId = getMmkBookCanisterId(getEnvMap());
	const depthQ = useMmkBookDepth(6);
	const myBookOrdersQ = useMyMmkOrders(identity);
	const agentPlace = useAgentPlaceLimit();
	const agentCancel = useAgentCancelOrder();

	useEffect(() => {
		setScanMin(loadScanIntervalMin());
		setAutopilot(loadAutopilotEnabled());
		setSettingsUpdatedAt(loadHuntSettingsUpdatedAt());
	}, []);

	useEffect(() => {
		if (huntSaved.length) setLocalHunt(huntSaved);
	}, [huntSaved]);

	useEffect(() => {
		if (!hfSaved) return;
		setHfMin(e8ToHf(hfSaved.minE8).toFixed(2));
		setHfMax(e8ToHf(hfSaved.maxE8).toFixed(2));
	}, [hfSaved]);

	const activeKeys = keys.filter((k) => !k.revoked);
	const fee = stats ? Number(stats.privilegedCycleFee) : 100_000_000;
	const feeT = fee / 1e8;
	const waiverMax = Number(stats?.humanWaiverDailyMax ?? 50);
	const waiversLeft = Number(waivers);
	const waiversUsed = Math.max(0, waiverMax - waiversLeft);
	const spentToday = getAutopilotSpentTodayUsdm();
	const huntHelp = agentHuntHelpText({
		dailyCapUsdm: DEFAULT_AUTOPILOT_DAILY_CAP_USDM,
		spentTodayUsdm: spentToday,
	});

	const seizedRows = useMemo(
		() => aggregateSeizedFromTxHistory(txHistory),
		[txHistory],
	);

	function markSettingsUpdated() {
		const ts = Date.now();
		saveHuntSettingsUpdatedAt(ts);
		setSettingsUpdatedAt(ts);
	}

	const protocolWired = stats?.protocolSet === true;
	const protocolWireUnknown = stats == null;

	async function handleCreateAgent() {
		if (!isAuthenticated) {
			await login();
			return;
		}
		if (!gatewayOk) {
			toast.error("Agent gateway is not configured in this environment.");
			return;
		}
		if (stats && !stats.protocolSet) {
			toast.error(
				"Agent gateway is not wired to the protocol yet. An admin must run setProtocol, then try again.",
			);
			return;
		}
		register.mutate(
			{
				name: name.trim() || "my-agent",
				scopes: ["All"],
			},
			{
				onSuccess: () => {
					setLocalHunt(defaultHuntTargets());
					setHfMin(HUNT_HF_FLOOR.toFixed(2));
					setHfMax(HUNT_HF_CAP.toFixed(2));
				},
			},
		);
	}

	function saveHuntSettings() {
		const min = Number(hfMin);
		const max = Number(hfMax);
		if (localHunt.length === 0) {
			toast.error("Select at least one hunt target");
			return;
		}
		if (
			!Number.isFinite(min) ||
			!Number.isFinite(max) ||
			min < HUNT_HF_FLOOR ||
			max > HUNT_HF_CAP ||
			min >= max
		) {
			toast.error("HF range must satisfy 1.00 ≤ min < max ≤ 1.30");
			return;
		}
		setHunt.mutate(localHunt, {
			onSuccess: () => {
				setHfRange.mutate(
					{ minE8: hfToE8(min), maxE8: hfToE8(max) },
					{
						onSuccess: () => {
							markSettingsUpdated();
							toast.success("Hunt settings saved");
						},
					},
				);
			},
		});
	}

	function toggleAsset(a: AgentCollateral) {
		setLocalHunt((prev) =>
			hasAsset(prev, a)
				? prev.filter((t) => !(t.kind === "asset" && t.asset === a))
				: [...prev, { kind: "asset", asset: a }],
		);
	}

	function toggleCombo(c: HuntComboId) {
		setLocalHunt((prev) =>
			hasCombo(prev, c)
				? prev.filter((t) => !(t.kind === "combo" && t.combo === c))
				: [...prev, { kind: "combo", combo: c }],
		);
	}

	if (agentLoading && isAuthenticated) {
		return (
			<div className="container mx-auto max-w-2xl px-4 py-12">
				<div className="h-40 animate-pulse rounded-2xl bg-yellow-400/10" />
			</div>
		);
	}

	if (!agent) {
		return (
			<div className="container mx-auto max-w-2xl space-y-6 px-4 py-8 pb-16">
				<div>
					<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/80">
						{AGENT_LAYER_SLOGAN}
					</p>
					<h1 className="mt-1 font-mono text-xl font-bold text-yellow-400 sm:text-2xl">
						My Agents
					</h1>
					<p className="mt-2 font-mono text-sm text-yellow-400/70">
						{AGENT_FLEET_BLURB}
					</p>
				</div>

				{!gatewayOk ? (
					<p className="rounded-xl border border-yellow-500/40 bg-yellow-400/10 px-4 py-3 font-mono text-xs text-yellow-200">
						Set <code>VITE_CANISTER_ID_AGENT_GATEWAY</code> for live agents.
					</p>
				) : null}

				<section className="relative overflow-hidden rounded-2xl border-2 border-yellow-400 bg-black p-6 sm:p-8">
					<div
						className="pointer-events-none absolute inset-0 opacity-10"
						style={{
							backgroundImage:
								"radial-gradient(circle at 20% 20%, #facc15, transparent 50%)",
						}}
					/>
					<div className="relative space-y-4">
						<p className="font-mono text-[10px] font-bold uppercase text-yellow-400/80">
							Get started
						</p>
						<h2 className="font-mono text-lg font-bold text-yellow-400 sm:text-xl">
							Create your personal agent
						</h2>
						<p className="max-w-md font-mono text-sm text-yellow-400/65">
							One agent on your Internet Identity. Then add API keys for bots
							and skills. Privileged calls cost ~{feeT.toFixed(2)} T cycles
							(credits or waivers).
						</p>
						{gatewayOk && stats && !protocolWired ? (
							<div
								role="alert"
								className="max-w-lg rounded-md border border-red-700 bg-red-950 px-3 py-2.5 font-mono text-xs text-red-50"
							>
								<p className="font-bold text-red-100">
									Gateway not wired to protocol
								</p>
								<p className="mt-1 text-red-100/90">
									Creating an agent needs admin{" "}
									<code className="text-red-50">setProtocol</code> (or{" "}
									<code className="text-red-50">update-platform.sh play</code>
									). Refresh after wiring.
								</p>
								<pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded bg-black/40 p-2 text-[10px] leading-relaxed text-red-100/85">{`PROTO=$(icp canister id protocol -e play)
icp canister call agent_gateway setProtocol "(principal \\"$PROTO\\")" -e play
GW=$(icp canister id agent_gateway -e play)
icp canister call protocol setAgentGateway "(principal \\"$GW\\")" -e play`}</pre>
							</div>
						) : null}
						<label className="block font-mono text-xs text-yellow-400/70">
							Display name
							<input
								value={name}
								onChange={(e) => setName(e.target.value)}
								className="mt-1 block h-11 w-full max-w-xs rounded-md border border-yellow-500/40 bg-black px-3 font-mono text-sm text-yellow-100"
							/>
						</label>
						<button
							type="button"
							disabled={
								register.isPending ||
								isLoggingIn ||
								!gatewayOk ||
								(!protocolWireUnknown && !protocolWired)
							}
							className="inline-flex min-h-12 items-center rounded-md bg-yellow-400 px-6 font-mono text-sm font-bold text-black hover:bg-yellow-300 disabled:opacity-40"
							onClick={() => void handleCreateAgent()}
						>
							{isLoggingIn || register.isPending
								? "Working…"
								: isAuthenticated
									? "Create agent"
									: "Connect & create agent"}
						</button>
					</div>
				</section>

				<p className="font-mono text-xs text-yellow-400/45">
					<Link to={ROUTES.skills} className="underline hover:text-yellow-300">
						Skills
					</Link>
					{" · "}
					<Link
						to={ROUTES.liquidity}
						className="underline hover:text-yellow-300"
					>
						Liquidity inventory
					</Link>
				</p>
			</div>
		);
	}

	const savingHunt = setHunt.isPending || setHfRange.isPending;

	return (
		<div className="container mx-auto max-w-2xl space-y-5 px-4 py-8 pb-16">
			{/* Ops status — gateway wiring + fee sustainability */}
			<div className="rounded-xl border border-yellow-500/25 bg-yellow-400/5 px-3 py-2 font-mono text-[11px] text-yellow-200/80">
				<p>
					<span
						className={
							gatewayOk ? "text-emerald-400" : "text-red-400"
						}
					>
						{gatewayOk ? "● Gateway configured" : "○ Gateway env missing"}
					</span>
					{" · "}
					privileged fee ~{feeT.toFixed(2)} T cycles/call · human waivers{" "}
					{waiversLeft}/{waiverMax}/day
					{stats?.paused ? (
						<span className="text-red-400"> · gateway paused</span>
					) : null}
				</p>
				<p className="mt-1 text-yellow-400/55">
					Fees fund protocol treasury ops (cycles / surplus path). Query
					opportunities free; privileged liq/hunt/book calls pay fee or waiver.
					Autopilot needs an open tab + active agent — use API keys for unattended
					bots.
				</p>
			</div>

			{/* MMK Book — agent path (cycle commission) */}
			<section className="rounded-2xl border border-yellow-500/30 bg-black/80 p-4 sm:p-5">
				<div className="flex flex-wrap items-start justify-between gap-2">
					<div>
						<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/80">
							MMK Book · agent
						</p>
						<h2 className="mt-1 font-mono text-sm font-bold text-yellow-400">
							Limit orders via gateway
						</h2>
						<p className="mt-1 max-w-md font-mono text-[11px] text-yellow-400/60">
							Each place/cancel charges ~{feeT.toFixed(2)} T cycles (credits or
							human waiver). Reading depth is free. Fills match on place —
							no separate fulfill call. Scope All includes MmkBook.
						</p>
					</div>
					<Link
						to={ROUTES.mmkBook}
						className="font-mono text-[11px] font-bold text-yellow-400 underline hover:text-yellow-300"
					>
						Full book UI
					</Link>
				</div>
				{!bookId ? (
					<p className="mt-3 font-mono text-xs text-amber-300/90">
						mmk_book not in SPA env — deploy/wire and refresh assets.
					</p>
				) : (
					<>
						<div className="mt-3 grid grid-cols-2 gap-3 font-mono text-[10px] text-yellow-400/70">
							<div>
								<p className="mb-1 text-emerald-400/90">Bids</p>
								<ul className="space-y-0.5">
									{(depthQ.data?.bids ?? []).slice(0, 4).map((r, i) => (
										<li key={`ab-${i}`} className="flex gap-2 tabular-nums">
											<span>
												{formatAmountPlain(r.price, 4)}{" "}
												<span className="text-yellow-400/45">USDM</span>
											</span>
											<span>
												{formatAmountPlain(r.size, 2)}{" "}
												<span className="text-yellow-400/45">MMK</span>
											</span>
										</li>
									))}
									{(depthQ.data?.bids?.length ?? 0) === 0 && (
										<li className="text-yellow-400/40">Empty</li>
									)}
								</ul>
							</div>
							<div>
								<p className="mb-1 text-rose-400/90">Asks</p>
								<ul className="space-y-0.5">
									{(depthQ.data?.asks ?? []).slice(0, 4).map((r, i) => (
										<li key={`aa-${i}`} className="flex gap-2 tabular-nums">
											<span>
												{formatAmountPlain(r.price, 4)}{" "}
												<span className="text-yellow-400/45">USDM</span>
											</span>
											<span>
												{formatAmountPlain(r.size, 2)}{" "}
												<span className="text-yellow-400/45">MMK</span>
											</span>
										</li>
									))}
									{(depthQ.data?.asks?.length ?? 0) === 0 && (
										<li className="text-yellow-400/40">Empty</li>
									)}
								</ul>
							</div>
						</div>
						<div className="mt-3 flex gap-2">
							<button
								type="button"
								className={`flex-1 rounded-md py-2 font-mono text-xs font-bold ${
									bookSide === "buy"
										? "bg-emerald-600 text-white"
										: "border border-yellow-500/40 text-yellow-400/70"
								}`}
								onClick={() => setBookSide("buy")}
							>
								Buy MMK
							</button>
							<button
								type="button"
								className={`flex-1 rounded-md py-2 font-mono text-xs font-bold ${
									bookSide === "sell"
										? "bg-rose-600 text-white"
										: "border border-yellow-500/40 text-yellow-400/70"
								}`}
								onClick={() => setBookSide("sell")}
							>
								Sell MMK
							</button>
						</div>
						<div className="mt-2 grid grid-cols-2 gap-2">
							<label className="font-mono text-[10px] text-yellow-400/60">
								Price USDM/MMK
								<input
									className="mt-1 w-full rounded-md border border-yellow-500/40 bg-black px-2 py-2 font-mono text-xs text-yellow-100"
									value={bookPrice}
									onChange={(e) => setBookPrice(e.target.value)}
								/>
							</label>
							<label className="font-mono text-[10px] text-yellow-400/60">
								Size MMK
								<input
									className="mt-1 w-full rounded-md border border-yellow-500/40 bg-black px-2 py-2 font-mono text-xs text-yellow-100"
									value={bookSize}
									onChange={(e) => setBookSize(e.target.value)}
								/>
							</label>
						</div>
						<p className="mt-2 font-mono text-[10px] text-yellow-400/50">
							Wallet {formatAmountPlain(balances.USDM ?? 0, 2)} USDM ·{" "}
							{formatAmountPlain(balances.MMK ?? 0, 2)} MMK · credits{" "}
							{(Number(credits) / 1e8).toFixed(2)} T · waivers {waiversLeft}
						</p>
						<button
							type="button"
							disabled={
								agentPlace.isPending || !agent.active || !bookId
							}
							className="mt-2 w-full rounded-md bg-yellow-400 py-2.5 font-mono text-xs font-bold text-black hover:bg-yellow-300 disabled:opacity-40"
							onClick={() => {
								const p = parseAmount(bookPrice);
								const s = parseAmount(bookSize);
								if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(s) || s <= 0) {
									toast.error("Valid price and size required");
									return;
								}
								// Settlement: agent must ICRC-2-approve mmk_book as spender first (same as human SPA).
								agentPlace.mutate({ side: bookSide, price: p, size: s });
							}}
						>
							{agentPlace.isPending
								? "Placing…"
								: `Place ${bookSide} (pays ~${feeT.toFixed(2)} T)`}
						</button>
						<ul className="mt-3 space-y-1.5 font-mono text-[11px]">
							{(myBookOrdersQ.data ?? [])
								.filter((o) => o.status === "open")
								.map((o) => (
									<li
										key={o.id}
										className="flex flex-wrap items-center justify-between gap-2 rounded border border-yellow-500/20 px-2 py-1.5 text-yellow-100/90"
									>
										<span>
											#{o.id} {o.side} @ {formatAmountPlain(o.price, 4)} ×{" "}
											{formatAmountPlain(o.size, 4)}
										</span>
										<button
											type="button"
											className="text-rose-400 underline disabled:opacity-40"
											disabled={agentCancel.isPending || !agent.active}
											onClick={() => agentCancel.mutate(o.id)}
										>
											Cancel (~{feeT.toFixed(2)} T)
										</button>
									</li>
								))}
						</ul>
					</>
				)}
			</section>

			{/* Header: name left · Active + toggle right */}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/80">
						{AGENT_LAYER_SLOGAN}
					</p>
					<h1 className="mt-1 font-mono text-xl font-bold text-yellow-400">
						{agent.name || "Agent"}
					</h1>
					<p className="mt-1 font-mono text-xs text-yellow-400/55">
						{principal ? `${principal.slice(0, 12)}…` : ""}
						{AGENT_GATEWAY_CANISTER_ID
							? ` · gw ${AGENT_GATEWAY_CANISTER_ID.slice(0, 8)}…`
							: ""}
						{" · "}~{feeT.toFixed(2)} T / call
					</p>
					<p className="mt-1 font-mono text-[11px]">
						<Link
							to={docsArticlePath("agents", "overview")}
							className="text-yellow-400 underline hover:text-yellow-300"
						>
							Docs · how it works
						</Link>
					</p>
				</div>
				<div className="flex flex-col items-end gap-2">
					<div className="flex items-center gap-2">
						<span
							className={cn(
								"inline-flex items-center rounded-full px-3 py-1 font-mono text-xs font-bold text-white",
								agent.active ? "bg-emerald-600/90" : "bg-red-600/90",
							)}
						>
							{agent.active ? "Active" : "Inactive"}
						</span>
						<InfoExplain label="" title="Active status">
							<p>{AGENT_ACTIVE_HELP}</p>
						</InfoExplain>
					</div>
					<button
						type="button"
						disabled={setActive.isPending}
						className={cn(
							"min-h-10 rounded-md px-4 font-mono text-xs font-bold disabled:opacity-40",
							agent.active
								? "border border-red-500/50 text-red-300 hover:bg-red-500/10"
								: "bg-emerald-500 text-white hover:bg-emerald-400",
						)}
						onClick={() => {
							if (agent.active) {
								const ok = window.confirm(
									"Deactivate agent? API keys and bots will fail privileged calls (liquidate, vault write) until you activate again. Human liquidate on /liquidate still works.",
								);
								if (!ok) return;
							}
							setActive.mutate(!agent.active);
						}}
					>
						{setActive.isPending
							? "Updating…"
							: agent.active
								? "Deactivate"
								: "Activate"}
					</button>
				</div>
			</div>

			{!agent.active ? (
				<section className="rounded-xl border border-red-500/40 bg-red-950/30 px-4 py-3 font-mono text-xs text-red-200">
					Agent inactive — Scan / Autopilot / API liquidate blocked. Prefs can
					still be saved. Human liquidate on{" "}
					<Link to={ROUTES.liquidation} className="underline">
						/liquidate
					</Link>{" "}
					still works.
				</section>
			) : null}

			{/* Cycle credits */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5 space-y-4">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					Cycle credits
				</h2>
				<div className="grid grid-cols-2 gap-2">
					<div className="rounded-lg border border-yellow-500/25 px-3 py-2">
						<p className="font-mono text-[10px] text-yellow-400/50">Credits</p>
						<p className="font-mono text-xl font-bold text-yellow-300">
							{String(credits)}
						</p>
					</div>
					<div className="rounded-lg border border-yellow-500/25 px-3 py-2">
						<p className="font-mono text-[10px] text-yellow-400/50">
							Waivers today
						</p>
						<p className="font-mono text-xl font-bold text-yellow-300">
							{waiversLeft}/{waiverMax}
						</p>
						<p className="font-mono text-[10px] text-yellow-400/45">
							used {waiversUsed}
						</p>
					</div>
				</div>
				<p className="font-mono text-[11px] text-yellow-400/50">
					{AGENT_FEE_SUSTAINABILITY}
				</p>
				<p className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-yellow-400/50">
					Buy credits with ICP. Wallet:
					<span className="inline-flex items-center gap-1.5 rounded-md border border-yellow-500/30 px-2 py-0.5 text-yellow-200">
						<TokenIcon token="ICP" size="sm" plain />
						ICP {balances.ICP.toFixed(4)}
					</span>
					<span className="inline-flex items-center gap-1.5 rounded-md border border-yellow-500/30 px-2 py-0.5 text-yellow-200">
						<TokenIcon token="USDM" size="sm" plain />
						USDM {balances.USDM.toFixed(2)}
					</span>
				</p>
				<div className="flex flex-wrap items-end gap-2">
					<label className="font-mono text-xs text-yellow-400/70">
						ICP amount
						<input
							value={icpAmount}
							onChange={(e) => setIcpAmount(e.target.value)}
							inputMode="decimal"
							className="mt-1 block h-10 w-28 rounded-md border border-yellow-500/40 bg-black px-3 font-mono text-sm text-yellow-100"
						/>
					</label>
					<button
						type="button"
						disabled={buyCredits.isPending || parseAmount(icpAmount) < 0.01}
						className="h-10 rounded-md bg-yellow-400 px-4 font-mono text-xs font-bold text-black disabled:opacity-40"
						onClick={() => buyCredits.mutate(parseAmount(icpAmount))}
					>
						{buyCredits.isPending ? "Buying…" : "Buy credits with ICP"}
					</button>
				</div>
			</section>

			{/* Hunt settings */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5 space-y-5">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<h2 className="font-mono text-sm font-bold text-yellow-400">
						Hunt settings
					</h2>
					<InfoExplain label="More info" title="Hunt & Autopilot">
						<p>{huntHelp}</p>
					</InfoExplain>
				</div>

				{/* 1. Mode */}
				<div>
					<p className="font-mono text-xs text-yellow-400/60">
						1 · Mode
					</p>
					<div className="mt-2 flex flex-wrap gap-2">
						<button
							type="button"
							onClick={() => {
								setAutopilot(false);
								saveAutopilotEnabled(false);
								notifyAutopilotChanged();
								markSettingsUpdated();
								toast.success("Watch mode — toast only, no auto-spend");
							}}
							className={cn(
								"rounded-full border px-3 py-1.5 font-mono text-xs font-bold",
								!autopilot
									? "border-yellow-400 bg-yellow-400 text-black"
									: "border-yellow-500/40 text-yellow-400",
							)}
						>
							Watch
						</button>
						<button
							type="button"
							disabled={!agent.active}
							onClick={() => {
								setAutopilot(true);
								saveAutopilotEnabled(true);
								notifyAutopilotChanged();
								markSettingsUpdated();
								toast.success(
									"Autopilot on — liquidates matches while this tab stays open",
								);
							}}
							className={cn(
								"rounded-full border px-3 py-1.5 font-mono text-xs font-bold disabled:opacity-40",
								autopilot
									? "border-yellow-400 bg-yellow-400 text-black"
									: "border-yellow-500/40 text-yellow-400",
							)}
						>
							Autopilot
						</button>
					</div>
				</div>

				{/* 2. Targets */}
				<div>
					<p className="font-mono text-xs text-yellow-400/60">
						2 · Singles — vaults that contain the asset
					</p>
					<div className="mt-2 flex flex-wrap gap-2">
						{COLS.map((c) => {
							const on = hasAsset(localHunt, c);
							return (
								<button
									key={c}
									type="button"
									onClick={() => toggleAsset(c)}
									className={cn(
										"rounded-full border px-3 py-1.5 font-mono text-xs font-bold",
										on
											? "border-yellow-400 bg-yellow-400 text-black"
											: "border-yellow-500/40 text-yellow-400",
									)}
								>
									{c}
								</button>
							);
						})}
					</div>
				</div>
				<div>
					<p className="font-mono text-xs text-yellow-400/60">
						2 · Multiasset — exact collateral mix
					</p>
					<div className="mt-2 flex flex-wrap gap-2">
						{COMBOS.map(({ id, label }) => {
							const on = hasCombo(localHunt, id);
							return (
								<button
									key={id}
									type="button"
									onClick={() => toggleCombo(id)}
									className={cn(
										"rounded-full border px-3 py-1.5 font-mono text-xs font-bold",
										on
											? "border-yellow-400 bg-yellow-400 text-black"
											: "border-yellow-500/40 text-yellow-400",
									)}
								>
									{label}
								</button>
							);
						})}
					</div>
					<p className="mt-2 font-mono text-[10px] text-yellow-400/45">
						Options are OR’d. Combos match exact collateral sets.
					</p>
				</div>

				{/* 3. HF */}
				<div>
					<p className="font-mono text-xs text-yellow-400/60">
						3 · Health Factor band (CR) · 1.00–1.30×
					</p>
					<div className="mt-2 flex flex-wrap items-end gap-2">
						<label className="font-mono text-xs text-yellow-400/70">
							Min
							<input
								value={hfMin}
								onChange={(e) => setHfMin(e.target.value)}
								inputMode="decimal"
								className="mt-1 block h-10 w-24 rounded-md border border-yellow-500/40 bg-black px-3 font-mono text-sm text-yellow-100"
							/>
						</label>
						<label className="font-mono text-xs text-yellow-400/70">
							Max
							<input
								value={hfMax}
								onChange={(e) => setHfMax(e.target.value)}
								inputMode="decimal"
								className="mt-1 block h-10 w-24 rounded-md border border-yellow-500/40 bg-black px-3 font-mono text-sm text-yellow-100"
							/>
						</label>
					</div>
				</div>

				{/* 4. Interval + status */}
				<div>
					<p className="font-mono text-xs text-yellow-400/60">
						4 · Check interval
					</p>
					<div className="mt-2 flex flex-wrap gap-1.5">
						{INTERVALS.map((m) => (
							<button
								key={m}
								type="button"
								onClick={() => {
									setScanMin(m);
									saveScanIntervalMin(m);
									notifyAutopilotChanged();
									markSettingsUpdated();
									toast.success(`Scan every ${m} min`);
								}}
								className={cn(
									"min-h-9 rounded-full border px-3 font-mono text-xs font-semibold",
									scanMin === m
										? "border-yellow-400 bg-yellow-400 text-black"
										: "border-yellow-500/40 text-yellow-400",
								)}
							>
								{m}m
							</button>
						))}
					</div>
					<p className="mt-2 space-y-1 font-mono text-[10px] text-yellow-400/45">
						<span className="block">
							Status: {huntStatus.mode}
							{huntStatus.running ? " · scanning…" : ""}
						</span>
						<span className="block">
							{agent.active
								? huntStatus.lastScanMs
									? `Last scan ${formatNs(huntStatus.lastScanMs)} · ${
											huntStatus.lastMatchCount ?? 0
										} match${huntStatus.lastMatchCount === 1 ? "" : "es"}`
									: "Scan armed — first pass runs shortly"
								: "Activate agent to arm the scan loop"}
						</span>
						{huntStatus.lastError ? (
							<span className="block text-red-300">
								Last error: {huntStatus.lastError}
							</span>
						) : null}
						{huntStatus.lastAutopilotResult ? (
							<span className="block text-yellow-200">
								{huntStatus.lastAutopilotResult}
							</span>
						) : null}
					</p>
				</div>

				{/* 5. Save + last updated */}
				<div className="border-t border-yellow-500/20 pt-4">
					<button
						type="button"
						disabled={savingHunt || localHunt.length === 0}
						className="w-full min-h-11 rounded-md bg-yellow-400 font-mono text-sm font-bold text-black hover:bg-yellow-300 disabled:opacity-40"
						onClick={saveHuntSettings}
					>
						{savingHunt ? "Saving…" : "Save hunt settings"}
					</button>
					<p className="mt-2 font-mono text-[10px] text-yellow-400/45">
						Last settings update:{" "}
						{settingsUpdatedAt
							? new Date(settingsUpdatedAt).toLocaleString()
							: "—"}
					</p>
				</div>
			</section>

			{/* API keys */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5 space-y-3">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					API keys
				</h2>
				<p className="font-mono text-[10px] text-yellow-400/45">
					{AGENT_KEY_SECURITY} For 24/7 bots see{" "}
					<Link to={ROUTES.skills} className="underline text-yellow-300">
						Skills
					</Link>{" "}
					and Minimac docs.
				</p>
				{newSecret ? (
					<div className="rounded-lg border border-yellow-400/50 bg-yellow-400/10 p-3">
						<p className="font-mono text-[10px] font-bold text-yellow-300">
							Copy secret now — shown once
						</p>
						<code className="mt-1 block break-all font-mono text-xs text-yellow-100">
							{newSecret}
						</code>
						<button
							type="button"
							className="mt-2 font-mono text-[11px] text-yellow-400 underline"
							onClick={() => {
								void copyToClipboard(newSecret).then((ok) =>
									ok
										? toast.success("Copied")
										: toast.error("Copy failed"),
								);
							}}
						>
							Copy
						</button>
					</div>
				) : null}
				<div className="flex flex-wrap items-end gap-2">
					<label className="font-mono text-xs text-yellow-400/70">
						Label
						<input
							value={keyLabel}
							onChange={(e) => setKeyLabel(e.target.value)}
							className="mt-1 block h-10 w-36 rounded-md border border-yellow-500/40 bg-black px-3 font-mono text-sm text-yellow-100"
						/>
					</label>
					<button
						type="button"
						disabled={createKey.isPending || !agent.active}
						className="h-10 rounded-md bg-yellow-400 px-4 font-mono text-xs font-bold text-black disabled:opacity-40"
						onClick={() =>
							createKey.mutate(keyLabel.trim() || "bot", {
								onSuccess: (secret) => {
									setNewSecret(secret);
									toast.success("API key created");
								},
							})
						}
					>
						{createKey.isPending ? "Creating…" : "Create key"}
					</button>
				</div>
				{keys.length > 0 ? (
					<ul className="space-y-1.5">
						{keys.map((k) => (
							<li
								key={k.id}
								className="flex items-center justify-between gap-2 rounded-lg border border-yellow-500/20 px-3 py-2 font-mono text-[11px]"
							>
								<span className="text-yellow-200">
									{k.keyLabel}
									{k.revoked ? (
										<span className="ml-2 text-red-300">revoked</span>
									) : (
										<span className="ml-2 text-emerald-400">active</span>
									)}
								</span>
								{!k.revoked ? (
									<button
										type="button"
										className="text-yellow-400/70 underline"
										onClick={() => revokeKey.mutate(k.id)}
									>
										Revoke
									</button>
								) : null}
							</li>
						))}
					</ul>
				) : (
					<p className="font-mono text-xs text-yellow-400/40">
						No keys yet · {activeKeys.length} active
					</p>
				)}
			</section>

			{/* Seized collateral table */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					Seized collateral
				</h2>
				<p className="mt-1 font-mono text-[10px] text-yellow-400/45">
					Aggregated from bot txs on this page.
				</p>
				{txsLoading ? (
					<div className="mt-3 h-12 animate-pulse rounded bg-yellow-400/10" />
				) : seizedRows.length === 0 ? (
					<p className="mt-2 font-mono text-xs text-yellow-400/45">
						No seized collateral in loaded bot transactions yet.
					</p>
				) : (
					<table className="mt-3 w-full font-mono text-[11px]">
						<thead>
							<tr className="border-b border-yellow-500/25 text-left text-yellow-400/55">
								<th className="py-1.5 pr-2 font-semibold">Token</th>
								<th className="py-1.5 pr-2 font-semibold">Amount</th>
								<th className="py-1.5 font-semibold">Txs</th>
							</tr>
						</thead>
						<tbody>
							{seizedRows.map((r) => (
								<tr
									key={r.token}
									className="border-b border-yellow-500/15 text-yellow-200"
								>
									<td className="py-2 pr-2">{r.token}</td>
									<td className="py-2 pr-2 tabular-nums">
										{formatTokenE8(r.amountE8, r.token)}
									</td>
									<td className="py-2 tabular-nums">{r.txCount}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</section>

			{/* Bot transactions */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					Bot transactions
				</h2>
				<p className="mt-1 font-mono text-[10px] text-yellow-400/45">
					Economic actions (liquidate, fill, vault writes). Tap a row for spent /
					seized / profit.
				</p>
				{txsLoading ? (
					<div className="mt-3 h-12 animate-pulse rounded bg-yellow-400/10" />
				) : txHistory.length === 0 ? (
					<p className="mt-2 font-mono text-xs text-yellow-400/45">
						No bot transactions yet.
					</p>
				) : (
					<ul className="mt-3 space-y-1.5">
						{txHistory.map((e) => {
							const isLiq =
								e.kind === "Liquidate" || e.kind === "FillAuction";
							const ts = e.ts > 1e15 ? e.ts / 1e6 : e.ts;
							return (
								<li key={`tx-${e.id}`}>
									<button
										type="button"
										className="flex w-full justify-between gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-left font-mono text-[11px] hover:border-emerald-400/40"
										onClick={() => setDetailTx(e)}
									>
										<span className="min-w-0">
											<span className="mr-1.5 rounded bg-emerald-500/20 px-1 py-0.5 text-[9px] font-bold uppercase text-emerald-200">
												tx
											</span>
											<span className="text-yellow-300">
												{e.kind}
												{e.vaultId != null ? ` · vault ${e.vaultId}` : ""}
											</span>
											<span className="mt-0.5 block truncate text-yellow-400/45">
												{isLiq
													? formatLiqTxActivitySub(e.detail, e.usdmSpentE8)
													: e.detail || "economic action"}
											</span>
										</span>
										<span className="shrink-0 text-yellow-400/45">
											{formatNs(ts)}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				)}
				<HistoryPager
					page={txPageIdx}
					pageSize={HISTORY_PAGE_SIZE}
					total={txTotal}
					onPrev={() => setTxPageIdx((p) => Math.max(0, p - 1))}
					onNext={() =>
						setTxPageIdx((p) =>
							Math.min(p + 1, Math.max(0, Math.ceil(txTotal / HISTORY_PAGE_SIZE) - 1)),
						)
					}
				/>
			</section>

			{/* Privileged calls */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					Privileged calls
				</h2>
				<p className="mt-1 font-mono text-[10px] text-yellow-400/45">
					Gateway fee audit (waiver / credits / cycles). Separate from economic
					txs.
				</p>
				{callsLoading ? (
					<div className="mt-3 h-12 animate-pulse rounded bg-yellow-400/10" />
				) : callHistory.length === 0 ? (
					<p className="mt-2 font-mono text-xs text-yellow-400/45">
						No privileged calls yet.
					</p>
				) : (
					<ul className="mt-3 space-y-1.5">
						{callHistory.map((e) => {
							const ts = e.ts > 1e15 ? e.ts / 1e6 : e.ts;
							const detail = stripFeePrefix(e.detail);
							return (
								<li
									key={`call-${e.id}`}
									className="flex justify-between gap-2 rounded-lg border border-amber-500/20 px-3 py-2 font-mono text-[11px]"
								>
									<span className="min-w-0">
										<span className="mr-1.5 rounded bg-amber-500/20 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-200">
											call
										</span>
										<span className="text-yellow-300">
											{e.ok ? "ok" : "fail"} · {e.method} ·{" "}
											{feeLabel(e.feeKind, e.cyclesCharged)}
										</span>
										<span className="mt-0.5 block truncate text-yellow-400/45">
											{detail || "privileged gateway call"}
										</span>
									</span>
									<span className="shrink-0 text-yellow-400/45">
										{formatNs(ts)}
									</span>
								</li>
							);
						})}
					</ul>
				)}
				<HistoryPager
					page={callPageIdx}
					pageSize={HISTORY_PAGE_SIZE}
					total={callTotal}
					onPrev={() => setCallPageIdx((p) => Math.max(0, p - 1))}
					onNext={() =>
						setCallPageIdx((p) =>
							Math.min(
								p + 1,
								Math.max(0, Math.ceil(callTotal / HISTORY_PAGE_SIZE) - 1),
							),
						)
					}
				/>
			</section>

			{/* Scans */}
			<section className="rounded-xl border border-yellow-500/30 bg-black/80 p-5">
				<h2 className="font-mono text-sm font-bold text-yellow-400">
					Scans
				</h2>
				<p className="mt-1 font-mono text-[10px] text-yellow-400/45">
					Local hunt log (this browser). Free protocol queries — no gateway fee.
				</p>
				{scanTotal === 0 ? (
					<p className="mt-2 font-mono text-xs text-yellow-400/45">
						No scans yet. Activate + Watch/Autopilot to populate.
					</p>
				) : (
					<ul className="mt-3 space-y-1.5">
						{scanSlice.map((s) => (
							<li
								key={s.id}
								className="flex justify-between gap-2 rounded-lg border border-yellow-500/15 px-3 py-2 font-mono text-[11px]"
							>
								<span className="min-w-0">
									<span className="mr-1.5 rounded bg-yellow-400/15 px-1 py-0.5 text-[9px] font-bold uppercase text-yellow-200">
										scan
									</span>
									<span className="text-yellow-300">
										{s.mode} · {s.matchCount} match
										{s.matchCount === 1 ? "" : "es"}
									</span>
									<span className="mt-0.5 block truncate text-yellow-400/45">
										{s.vaultIds.length > 0
											? `vaults ${s.vaultIds.map((id) => `#${id}`).join(", ")}`
											: "no matches"}
									</span>
								</span>
								<span className="shrink-0 text-yellow-400/45">
									{formatNs(s.ts)}
								</span>
							</li>
						))}
					</ul>
				)}
				<HistoryPager
					page={scanPageIdx}
					pageSize={HISTORY_PAGE_SIZE}
					total={scanTotal}
					onPrev={() => setScanPageIdx((p) => Math.max(0, p - 1))}
					onNext={() =>
						setScanPageIdx((p) =>
							Math.min(
								p + 1,
								Math.max(0, Math.ceil(scanTotal / HISTORY_PAGE_SIZE) - 1),
							),
						)
					}
				/>
			</section>

			<p className="font-mono text-xs text-yellow-400/45">
				<Link to={ROUTES.skills} className="underline hover:text-yellow-300">
					Skills
				</Link>
				{" · "}
				<Link to={ROUTES.liquidity} className="underline hover:text-yellow-300">
					Liquidity inventory
				</Link>
				{" · "}
				<Link
					to={ROUTES.liquidation}
					className="underline hover:text-yellow-300"
				>
					Liquidate
				</Link>
				{" · "}
				<Link to={ROUTES.docs} className="underline hover:text-yellow-300">
					Docs
				</Link>
			</p>

			<LiqTxDetailDialog tx={detailTx} onClose={() => setDetailTx(null)} />
		</div>
	);
}
